import { forwardRef, useCallback, useMemo } from 'react';
import { Empty, Table } from 'antd';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import { CALENDAR_TABLE_HEIGHT_OFFSET } from '../../config/calendarConstants';
import { CHANGE_TYPE_CONFIG } from '../../config/changeTypeConfig';
import type { CalendarRow, PromoTypeLegendItem } from '../../types/calendar';
import {
    TPO_CALENDAR_EMPTY,
    TPO_CALENDAR_LOADING,
    TPO_CALENDAR_SCROLLER,
    TPO_CALENDAR_TABLE,
    tpoTableRowTestId,
} from '../../config/testIds';
import PromoTypeLegend from './components/PromoTypeLegend';

export interface CalendarTableProps {
    dataSource: CalendarRow[];
    columns: any[];
    isPending: boolean;
    isFetching: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean;
    fetchNextPage: () => void;
    expandedRowKeys: string[];
    setExpandedRowKeys: (keys: string[]) => void;
    handleTableChange: any;
    legend: PromoTypeLegendItem[];
    showChanges: boolean;
}

const CalendarTable = forwardRef<HTMLDivElement, CalendarTableProps>(
    (
        {
            dataSource,
            columns,
            isPending,
            isFetching,
            isFetchingNextPage,
            hasNextPage,
            fetchNextPage,
            expandedRowKeys,
            setExpandedRowKeys,
            handleTableChange,
            legend,
            showChanges,
        },
        ref,
    ) => {
        const getRowClassName = useCallback(
            (record: CalendarRow) => {
                if (!showChanges || record.children || !record.promo_lifecycle_status) return '';
                return CHANGE_TYPE_CONFIG[record.promo_lifecycle_status]?.rowClassName ?? '';
            },
            [showChanges],
        );

        const isTableLoading = isPending || isFetching || isFetchingNextPage;

        const tableLocale = useMemo(
            () => ({
                emptyText: isTableLoading ? (
                    <div data-testid={TPO_CALENDAR_LOADING} />
                ) : (
                    <div data-testid={TPO_CALENDAR_EMPTY}>
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </div>
                ),
            }),
            [isTableLoading],
        );

        return (
            <div className="h-full" ref={ref}>
                <InfiniteScroller
                    data={dataSource}
                    onNext={fetchNextPage}
                    hasMore={hasNextPage}
                    loading={isFetchingNextPage}
                    height={window.innerHeight - CALENDAR_TABLE_HEIGHT_OFFSET}
                    data-testid={TPO_CALENDAR_SCROLLER}
                >
                    <Table
                        className="planner-calendar-table"
                        pagination={false}
                        dataSource={dataSource}
                        columns={columns}
                        scroll={{ x: 'max-content' }}
                        loading={isTableLoading}
                        locale={tableLocale}
                        sticky={{
                            getContainer: () =>
                                document.getElementsByClassName('infinite-scroll-component')?.[0] as HTMLElement,
                        }}
                        bordered
                        onChange={handleTableChange}
                        rowClassName={getRowClassName}
                        data-testid={TPO_CALENDAR_TABLE}
                        onRow={(record: CalendarRow) =>
                            ({
                                'data-testid': tpoTableRowTestId('calendar', record.key),
                                // Surfaces the added/edited/deleted marker that "Show
                                // Changes" renders only as a row colour.
                                'data-state-change': record.promo_lifecycle_status || undefined,
                            } as React.HTMLAttributes<HTMLTableRowElement>)
                        }
                        expandable={{
                            expandedRowKeys,
                            onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
                            showExpandColumn: false,
                        }}
                    />
                </InfiniteScroller>
                <PromoTypeLegend items={legend} />
            </div>
        );
    },
);

CalendarTable.displayName = 'CalendarTable';
export default CalendarTable;
